/**
 * SEO Audit Utility for SITESAPP
 *
 * This utility provides functions to audit and score SEO implementation
 * across customer websites. It can be used both in development and as
 * part of CI/CD pipelines.
 */

export interface SEOAuditResult {
  score: number;
  maxScore: number;
  percentage: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  categories: SEOCategory[];
  issues: SEOIssue[];
  recommendations: string[];
}

export interface SEOCategory {
  name: string;
  score: number;
  maxScore: number;
  checks: SEOCheck[];
}

export interface SEOCheck {
  name: string;
  passed: boolean;
  points: number;
  message: string;
  severity: 'critical' | 'warning' | 'info';
}

export interface SEOIssue {
  category: string;
  check: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  recommendation: string;
}

interface DocumentMeta {
  title?: string;
  description?: string;
  canonical?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: unknown[];
  h1Count?: number;
  h2Count?: number;
  imagesMissingAlt?: number;
  totalImages?: number;
  internalLinks?: number;
  externalLinks?: number;
}

/**
 * Extracts SEO-relevant metadata from the current document
 * This runs in the browser context
 */
export function extractDocumentMeta(): DocumentMeta {
  if (typeof document === 'undefined') {
    return {};
  }

  const getMeta = (name: string, property?: string): string | undefined => {
    const byName = document.querySelector(`meta[name="${name}"]`);
    if (byName) return byName.getAttribute('content') || undefined;

    if (property) {
      const byProperty = document.querySelector(`meta[property="${property}"]`);
      if (byProperty) return byProperty.getAttribute('content') || undefined;
    }

    return undefined;
  };

  // Extract structured data
  const structuredData: unknown[] = [];
  document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
    try {
      structuredData.push(JSON.parse(script.textContent || ''));
    } catch {
      // Invalid JSON, skip
    }
  });

  // Count headings
  const h1Count = document.querySelectorAll('h1').length;
  const h2Count = document.querySelectorAll('h2').length;

  // Count images and check for alt text
  const images = document.querySelectorAll('img');
  let imagesMissingAlt = 0;
  images.forEach((img) => {
    if (!img.getAttribute('alt')) {
      imagesMissingAlt++;
    }
  });

  // Count links
  const links = document.querySelectorAll('a[href]');
  let internalLinks = 0;
  let externalLinks = 0;
  const currentHost = window.location.host;

  links.forEach((link) => {
    const href = link.getAttribute('href') || '';
    if (href.startsWith('/') || href.includes(currentHost)) {
      internalLinks++;
    } else if (href.startsWith('http')) {
      externalLinks++;
    }
  });

  return {
    title: document.title,
    description: getMeta('description'),
    canonical:
      document.querySelector('link[rel="canonical"]')?.getAttribute('href') ||
      undefined,
    robots: getMeta('robots'),
    ogTitle: getMeta('og:title', 'og:title'),
    ogDescription: getMeta('og:description', 'og:description'),
    ogImage: getMeta('og:image', 'og:image'),
    ogUrl: getMeta('og:url', 'og:url'),
    twitterCard: getMeta('twitter:card'),
    twitterTitle: getMeta('twitter:title'),
    twitterDescription: getMeta('twitter:description'),
    twitterImage: getMeta('twitter:image'),
    structuredData,
    h1Count,
    h2Count,
    imagesMissingAlt,
    totalImages: images.length,
    internalLinks,
    externalLinks,
  };
}

/**
 * Audits SEO implementation based on extracted metadata
 */
export function auditSEO(meta: DocumentMeta): SEOAuditResult {
  const categories: SEOCategory[] = [];
  const issues: SEOIssue[] = [];

  // Category 1: Basic Meta Tags (25 points)
  const basicMeta: SEOCheck[] = [];

  // Title check (5 points)
  if (meta.title) {
    const titleLength = meta.title.length;
    if (titleLength >= 30 && titleLength <= 60) {
      basicMeta.push({
        name: 'Title Tag',
        passed: true,
        points: 5,
        message: `Title tag present and optimal length (${titleLength} chars)`,
        severity: 'info',
      });
    } else {
      basicMeta.push({
        name: 'Title Tag',
        passed: true,
        points: 3,
        message: `Title tag present but length is ${titleLength} chars (ideal: 30-60)`,
        severity: 'warning',
      });
      issues.push({
        category: 'Basic Meta Tags',
        check: 'Title Tag',
        severity: 'warning',
        message: `Title length is ${titleLength} characters`,
        recommendation:
          'Keep title between 30-60 characters for optimal display in search results',
      });
    }
  } else {
    basicMeta.push({
      name: 'Title Tag',
      passed: false,
      points: 0,
      message: 'Missing title tag',
      severity: 'critical',
    });
    issues.push({
      category: 'Basic Meta Tags',
      check: 'Title Tag',
      severity: 'critical',
      message: 'Page is missing a title tag',
      recommendation: 'Add a unique, descriptive title tag to every page',
    });
  }

  // Description check (5 points)
  if (meta.description) {
    const descLength = meta.description.length;
    if (descLength >= 120 && descLength <= 160) {
      basicMeta.push({
        name: 'Meta Description',
        passed: true,
        points: 5,
        message: `Meta description present and optimal length (${descLength} chars)`,
        severity: 'info',
      });
    } else {
      basicMeta.push({
        name: 'Meta Description',
        passed: true,
        points: 3,
        message: `Meta description present but length is ${descLength} chars (ideal: 120-160)`,
        severity: 'warning',
      });
      issues.push({
        category: 'Basic Meta Tags',
        check: 'Meta Description',
        severity: 'warning',
        message: `Description length is ${descLength} characters`,
        recommendation:
          'Keep meta description between 120-160 characters for optimal display',
      });
    }
  } else {
    basicMeta.push({
      name: 'Meta Description',
      passed: false,
      points: 0,
      message: 'Missing meta description',
      severity: 'critical',
    });
    issues.push({
      category: 'Basic Meta Tags',
      check: 'Meta Description',
      severity: 'critical',
      message: 'Page is missing a meta description',
      recommendation:
        'Add a unique, compelling meta description to every page',
    });
  }

  // Canonical URL check (5 points)
  if (meta.canonical) {
    basicMeta.push({
      name: 'Canonical URL',
      passed: true,
      points: 5,
      message: 'Canonical URL is set',
      severity: 'info',
    });
  } else {
    basicMeta.push({
      name: 'Canonical URL',
      passed: false,
      points: 0,
      message: 'Missing canonical URL',
      severity: 'warning',
    });
    issues.push({
      category: 'Basic Meta Tags',
      check: 'Canonical URL',
      severity: 'warning',
      message: 'Page is missing a canonical URL',
      recommendation:
        'Add a canonical URL to prevent duplicate content issues',
    });
  }

  // Robots meta check (5 points)
  if (meta.robots) {
    if (meta.robots.includes('index') && meta.robots.includes('follow')) {
      basicMeta.push({
        name: 'Robots Meta',
        passed: true,
        points: 5,
        message: 'Page is indexable and followable',
        severity: 'info',
      });
    } else {
      basicMeta.push({
        name: 'Robots Meta',
        passed: true,
        points: 3,
        message: `Robots directive: ${meta.robots}`,
        severity: 'info',
      });
    }
  } else {
    basicMeta.push({
      name: 'Robots Meta',
      passed: false,
      points: 2,
      message: 'No explicit robots directive (defaults to index,follow)',
      severity: 'info',
    });
  }

  // H1 check (5 points)
  if (meta.h1Count === 1) {
    basicMeta.push({
      name: 'H1 Heading',
      passed: true,
      points: 5,
      message: 'Single H1 heading found (best practice)',
      severity: 'info',
    });
  } else if (meta.h1Count && meta.h1Count > 1) {
    basicMeta.push({
      name: 'H1 Heading',
      passed: true,
      points: 3,
      message: `${meta.h1Count} H1 headings found (recommend only 1)`,
      severity: 'warning',
    });
    issues.push({
      category: 'Basic Meta Tags',
      check: 'H1 Heading',
      severity: 'warning',
      message: `Page has ${meta.h1Count} H1 headings`,
      recommendation: 'Use only one H1 heading per page for better SEO',
    });
  } else {
    basicMeta.push({
      name: 'H1 Heading',
      passed: false,
      points: 0,
      message: 'No H1 heading found',
      severity: 'critical',
    });
    issues.push({
      category: 'Basic Meta Tags',
      check: 'H1 Heading',
      severity: 'critical',
      message: 'Page is missing an H1 heading',
      recommendation: 'Add one H1 heading that describes the main content',
    });
  }

  categories.push({
    name: 'Basic Meta Tags',
    score: basicMeta.reduce((sum, check) => sum + check.points, 0),
    maxScore: 25,
    checks: basicMeta,
  });

  // Category 2: Social Media (20 points)
  const socialMeta: SEOCheck[] = [];

  // Open Graph (10 points)
  const ogChecks = [
    { name: 'og:title', value: meta.ogTitle },
    { name: 'og:description', value: meta.ogDescription },
    { name: 'og:image', value: meta.ogImage },
    { name: 'og:url', value: meta.ogUrl },
  ];

  const ogPassed = ogChecks.filter((check) => check.value).length;
  if (ogPassed === 4) {
    socialMeta.push({
      name: 'Open Graph Tags',
      passed: true,
      points: 10,
      message: 'All Open Graph tags present',
      severity: 'info',
    });
  } else if (ogPassed > 0) {
    socialMeta.push({
      name: 'Open Graph Tags',
      passed: true,
      points: Math.round((ogPassed / 4) * 10),
      message: `${ogPassed}/4 Open Graph tags present`,
      severity: 'warning',
    });
    const missing = ogChecks.filter((check) => !check.value).map((c) => c.name);
    issues.push({
      category: 'Social Media',
      check: 'Open Graph Tags',
      severity: 'warning',
      message: `Missing Open Graph tags: ${missing.join(', ')}`,
      recommendation: 'Add all Open Graph tags for better social media sharing',
    });
  } else {
    socialMeta.push({
      name: 'Open Graph Tags',
      passed: false,
      points: 0,
      message: 'No Open Graph tags found',
      severity: 'critical',
    });
    issues.push({
      category: 'Social Media',
      check: 'Open Graph Tags',
      severity: 'critical',
      message: 'Page is missing all Open Graph tags',
      recommendation:
        'Add og:title, og:description, og:image, and og:url for social sharing',
    });
  }

  // Twitter Card (10 points)
  const twitterChecks = [
    { name: 'twitter:card', value: meta.twitterCard },
    { name: 'twitter:title', value: meta.twitterTitle },
    { name: 'twitter:description', value: meta.twitterDescription },
    { name: 'twitter:image', value: meta.twitterImage },
  ];

  const twitterPassed = twitterChecks.filter((check) => check.value).length;
  if (twitterPassed === 4) {
    socialMeta.push({
      name: 'Twitter Card Tags',
      passed: true,
      points: 10,
      message: 'All Twitter Card tags present',
      severity: 'info',
    });
  } else if (twitterPassed > 0) {
    socialMeta.push({
      name: 'Twitter Card Tags',
      passed: true,
      points: Math.round((twitterPassed / 4) * 10),
      message: `${twitterPassed}/4 Twitter Card tags present`,
      severity: 'warning',
    });
    const missing = twitterChecks
      .filter((check) => !check.value)
      .map((c) => c.name);
    issues.push({
      category: 'Social Media',
      check: 'Twitter Card Tags',
      severity: 'warning',
      message: `Missing Twitter Card tags: ${missing.join(', ')}`,
      recommendation: 'Add all Twitter Card tags for better Twitter sharing',
    });
  } else {
    socialMeta.push({
      name: 'Twitter Card Tags',
      passed: false,
      points: 0,
      message: 'No Twitter Card tags found',
      severity: 'critical',
    });
    issues.push({
      category: 'Social Media',
      check: 'Twitter Card Tags',
      severity: 'critical',
      message: 'Page is missing all Twitter Card tags',
      recommendation: 'Add Twitter Card tags for better Twitter sharing',
    });
  }

  categories.push({
    name: 'Social Media',
    score: socialMeta.reduce((sum, check) => sum + check.points, 0),
    maxScore: 20,
    checks: socialMeta,
  });

  // Category 3: Structured Data (25 points)
  const structuredDataChecks: SEOCheck[] = [];

  if (meta.structuredData && meta.structuredData.length > 0) {
    structuredDataChecks.push({
      name: 'JSON-LD Present',
      passed: true,
      points: 10,
      message: `${meta.structuredData.length} structured data block(s) found`,
      severity: 'info',
    });

    // Check for specific schema types
    const schemaTypes = new Set<string>();
    meta.structuredData.forEach((data: unknown) => {
      const schema = data as { '@type'?: string | string[] };
      if (schema['@type']) {
        const types = Array.isArray(schema['@type'])
          ? schema['@type']
          : [schema['@type']];
        types.forEach((t: string) => schemaTypes.add(t));
      }
    });

    if (
      schemaTypes.has('LocalBusiness') ||
      schemaTypes.has('Organization') ||
      schemaTypes.has('WasteManagementCompany')
    ) {
      structuredDataChecks.push({
        name: 'Business Schema',
        passed: true,
        points: 5,
        message: 'LocalBusiness/Organization schema found',
        severity: 'info',
      });
    } else {
      structuredDataChecks.push({
        name: 'Business Schema',
        passed: false,
        points: 0,
        message: 'No LocalBusiness/Organization schema found',
        severity: 'warning',
      });
      issues.push({
        category: 'Structured Data',
        check: 'Business Schema',
        severity: 'warning',
        message: 'Missing LocalBusiness or Organization schema',
        recommendation:
          'Add LocalBusiness schema with business details for local SEO',
      });
    }

    if (schemaTypes.has('FAQPage')) {
      structuredDataChecks.push({
        name: 'FAQ Schema',
        passed: true,
        points: 5,
        message: 'FAQPage schema found',
        severity: 'info',
      });
    }

    if (schemaTypes.has('BreadcrumbList')) {
      structuredDataChecks.push({
        name: 'Breadcrumb Schema',
        passed: true,
        points: 5,
        message: 'BreadcrumbList schema found',
        severity: 'info',
      });
    }
  } else {
    structuredDataChecks.push({
      name: 'JSON-LD Present',
      passed: false,
      points: 0,
      message: 'No structured data found',
      severity: 'critical',
    });
    issues.push({
      category: 'Structured Data',
      check: 'JSON-LD Present',
      severity: 'critical',
      message: 'Page has no structured data',
      recommendation:
        'Add JSON-LD structured data for better search engine understanding',
    });
  }

  categories.push({
    name: 'Structured Data',
    score: structuredDataChecks.reduce((sum, check) => sum + check.points, 0),
    maxScore: 25,
    checks: structuredDataChecks,
  });

  // Category 4: Accessibility & Content (15 points)
  const accessibilityChecks: SEOCheck[] = [];

  // Image alt text (10 points)
  if (meta.totalImages && meta.totalImages > 0) {
    const altPercentage =
      ((meta.totalImages - (meta.imagesMissingAlt || 0)) / meta.totalImages) *
      100;
    if (altPercentage === 100) {
      accessibilityChecks.push({
        name: 'Image Alt Text',
        passed: true,
        points: 10,
        message: `All ${meta.totalImages} images have alt text`,
        severity: 'info',
      });
    } else if (altPercentage >= 80) {
      accessibilityChecks.push({
        name: 'Image Alt Text',
        passed: true,
        points: 7,
        message: `${Math.round(altPercentage)}% of images have alt text`,
        severity: 'warning',
      });
      issues.push({
        category: 'Accessibility & Content',
        check: 'Image Alt Text',
        severity: 'warning',
        message: `${meta.imagesMissingAlt} images are missing alt text`,
        recommendation: 'Add descriptive alt text to all images',
      });
    } else {
      accessibilityChecks.push({
        name: 'Image Alt Text',
        passed: false,
        points: Math.round((altPercentage / 100) * 10),
        message: `Only ${Math.round(altPercentage)}% of images have alt text`,
        severity: 'critical',
      });
      issues.push({
        category: 'Accessibility & Content',
        check: 'Image Alt Text',
        severity: 'critical',
        message: `${meta.imagesMissingAlt} of ${meta.totalImages} images are missing alt text`,
        recommendation:
          'Add descriptive alt text to all images for accessibility and SEO',
      });
    }
  } else {
    accessibilityChecks.push({
      name: 'Image Alt Text',
      passed: true,
      points: 10,
      message: 'No images on page (N/A)',
      severity: 'info',
    });
  }

  // Heading structure (5 points)
  if ((meta.h2Count || 0) >= 2) {
    accessibilityChecks.push({
      name: 'Heading Structure',
      passed: true,
      points: 5,
      message: `Good heading structure with ${meta.h2Count} H2 headings`,
      severity: 'info',
    });
  } else if ((meta.h2Count || 0) === 1) {
    accessibilityChecks.push({
      name: 'Heading Structure',
      passed: true,
      points: 3,
      message: 'Only 1 H2 heading found',
      severity: 'warning',
    });
    issues.push({
      category: 'Accessibility & Content',
      check: 'Heading Structure',
      severity: 'warning',
      message: 'Page has limited heading structure',
      recommendation:
        'Use more H2 headings to structure content for better SEO',
    });
  } else {
    accessibilityChecks.push({
      name: 'Heading Structure',
      passed: false,
      points: 0,
      message: 'No H2 headings found',
      severity: 'warning',
    });
    issues.push({
      category: 'Accessibility & Content',
      check: 'Heading Structure',
      severity: 'warning',
      message: 'Page has no H2 headings',
      recommendation:
        'Add H2 headings to structure content and improve readability',
    });
  }

  categories.push({
    name: 'Accessibility & Content',
    score: accessibilityChecks.reduce((sum, check) => sum + check.points, 0),
    maxScore: 15,
    checks: accessibilityChecks,
  });

  // Category 5: Links (15 points)
  const linkChecks: SEOCheck[] = [];

  // Internal links (10 points)
  if ((meta.internalLinks || 0) >= 5) {
    linkChecks.push({
      name: 'Internal Links',
      passed: true,
      points: 10,
      message: `Good internal linking with ${meta.internalLinks} internal links`,
      severity: 'info',
    });
  } else if ((meta.internalLinks || 0) >= 2) {
    linkChecks.push({
      name: 'Internal Links',
      passed: true,
      points: 5,
      message: `${meta.internalLinks} internal links found`,
      severity: 'warning',
    });
    issues.push({
      category: 'Links',
      check: 'Internal Links',
      severity: 'warning',
      message: 'Limited internal linking',
      recommendation: 'Add more internal links to improve site structure',
    });
  } else {
    linkChecks.push({
      name: 'Internal Links',
      passed: false,
      points: 0,
      message: `Only ${meta.internalLinks || 0} internal links found`,
      severity: 'critical',
    });
    issues.push({
      category: 'Links',
      check: 'Internal Links',
      severity: 'critical',
      message: 'Very few internal links on page',
      recommendation:
        'Add internal links to improve navigation and SEO link equity',
    });
  }

  // External links present (5 points - having some is good for credibility)
  if ((meta.externalLinks || 0) >= 1) {
    linkChecks.push({
      name: 'External Links',
      passed: true,
      points: 5,
      message: `${meta.externalLinks} external links found`,
      severity: 'info',
    });
  } else {
    linkChecks.push({
      name: 'External Links',
      passed: true,
      points: 3,
      message: 'No external links (not necessarily bad)',
      severity: 'info',
    });
  }

  categories.push({
    name: 'Links',
    score: linkChecks.reduce((sum, check) => sum + check.points, 0),
    maxScore: 15,
    checks: linkChecks,
  });

  // Calculate total score
  const totalScore = categories.reduce((sum, cat) => sum + cat.score, 0);
  const maxScore = categories.reduce((sum, cat) => sum + cat.maxScore, 0);
  const percentage = Math.round((totalScore / maxScore) * 100);

  // Determine grade
  let grade: 'A' | 'B' | 'C' | 'D' | 'F';
  if (percentage >= 90) grade = 'A';
  else if (percentage >= 80) grade = 'B';
  else if (percentage >= 70) grade = 'C';
  else if (percentage >= 60) grade = 'D';
  else grade = 'F';

  // Generate recommendations
  const recommendations: string[] = [];
  if (issues.filter((i) => i.severity === 'critical').length > 0) {
    recommendations.push(
      'Fix critical issues first - these significantly impact SEO performance'
    );
  }
  if (!meta.structuredData || meta.structuredData.length === 0) {
    recommendations.push(
      'Add JSON-LD structured data for rich search results'
    );
  }
  if (!meta.ogImage) {
    recommendations.push(
      'Add an Open Graph image for better social media sharing'
    );
  }
  if ((meta.imagesMissingAlt || 0) > 0) {
    recommendations.push('Add alt text to all images for accessibility and SEO');
  }

  return {
    score: totalScore,
    maxScore,
    percentage,
    grade,
    categories,
    issues,
    recommendations,
  };
}

/**
 * Formats the audit result as a readable report
 */
export function formatAuditReport(result: SEOAuditResult): string {
  const lines: string[] = [];

  lines.push('='.repeat(60));
  lines.push('SEO AUDIT REPORT');
  lines.push('='.repeat(60));
  lines.push('');
  lines.push(`Overall Score: ${result.score}/${result.maxScore} (${result.percentage}%)`);
  lines.push(`Grade: ${result.grade}`);
  lines.push('');

  // Category breakdown
  lines.push('-'.repeat(60));
  lines.push('CATEGORY BREAKDOWN');
  lines.push('-'.repeat(60));

  result.categories.forEach((category) => {
    const catPercentage = Math.round((category.score / category.maxScore) * 100);
    lines.push(`\n${category.name}: ${category.score}/${category.maxScore} (${catPercentage}%)`);
    category.checks.forEach((check) => {
      const icon = check.passed ? '✓' : '✗';
      lines.push(`  ${icon} ${check.name}: ${check.message}`);
    });
  });

  // Issues
  if (result.issues.length > 0) {
    lines.push('');
    lines.push('-'.repeat(60));
    lines.push('ISSUES FOUND');
    lines.push('-'.repeat(60));

    const criticalIssues = result.issues.filter((i) => i.severity === 'critical');
    const warningIssues = result.issues.filter((i) => i.severity === 'warning');

    if (criticalIssues.length > 0) {
      lines.push('\nCritical Issues:');
      criticalIssues.forEach((issue) => {
        lines.push(`  • [${issue.category}] ${issue.message}`);
        lines.push(`    → ${issue.recommendation}`);
      });
    }

    if (warningIssues.length > 0) {
      lines.push('\nWarnings:');
      warningIssues.forEach((issue) => {
        lines.push(`  • [${issue.category}] ${issue.message}`);
        lines.push(`    → ${issue.recommendation}`);
      });
    }
  }

  // Recommendations
  if (result.recommendations.length > 0) {
    lines.push('');
    lines.push('-'.repeat(60));
    lines.push('TOP RECOMMENDATIONS');
    lines.push('-'.repeat(60));
    result.recommendations.forEach((rec, i) => {
      lines.push(`${i + 1}. ${rec}`);
    });
  }

  lines.push('');
  lines.push('='.repeat(60));

  return lines.join('\n');
}

/**
 * Run a full SEO audit on the current page
 * Call this from the browser console or development tools
 */
export function runSEOAudit(): SEOAuditResult {
  const meta = extractDocumentMeta();
  return auditSEO(meta);
}

/**
 * Print a formatted SEO audit report to the console
 */
export function printSEOReport(): void {
  const result = runSEOAudit();
  console.log(formatAuditReport(result));
}

// Export for use in development
if (typeof window !== 'undefined') {
  (window as unknown as { seoAudit: typeof runSEOAudit; printSEOReport: typeof printSEOReport }).seoAudit = runSEOAudit;
  (window as unknown as { printSEOReport: typeof printSEOReport }).printSEOReport = printSEOReport;
}
